// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTheme } from "next-themes";
import { useCallback } from "react";
import { flushSync } from "react-dom";

export type ThemeChoice = "light" | "dark" | "system";

/** Where the reveal grows from: a point, an element's centre, or (by default) the top-right corner. */
export type RevealOrigin = { x: number; y: number } | Element | null | undefined;

// Same reveal as the docs site (astro-theme-toggle): a soft-edged circle, solid to 70% of its radius
const GRADIENT_OFFSET = 0.7;
const DURATION_MS = 500;
const MASK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8"><defs><radialGradient id="g"><stop offset="${GRADIENT_OFFSET}"/><stop offset="1" stop-opacity="0"/></radialGradient></defs><circle cx="4" cy="4" r="4" fill="url(#g)"/></svg>`;
const MASK_URL = `url('data:image/svg+xml;base64,${typeof window !== "undefined" ? window.btoa(MASK_SVG) : ""}')`;
// Stops the default cross-fade so only the mask reveals the new look
const STYLE_ID = "theme-reveal-styles";
const STYLE = "::view-transition-old(root),::view-transition-new(root){animation:none;mix-blend-mode:normal}";

/** The palette's close animation (`duration-200`): wait it out so the palette isn't in the old snapshot. */
export const DIALOG_CLOSE_MS = 200;

function originPoint(origin: RevealOrigin): { x: number; y: number } {
  if (origin instanceof Element) {
    const rect = origin.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  return origin ?? { x: window.innerWidth, y: 0 };
}

/**
 * Runs `apply` — which must update the DOM synchronously — and reveals the result in a circle
 * growing from `origin`. Without the View Transitions API, or with reduced motion, it just applies.
 */
export function revealChange(apply: () => void, origin?: RevealOrigin): void {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!document.startViewTransition || reduceMotion) {
    apply();
    return;
  }

  const { x, y } = originPoint(origin);
  document.getElementById(STYLE_ID)?.remove();
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = STYLE;
  document.head.appendChild(style);

  const transition = document.startViewTransition(apply);
  transition.finished.finally(() => style.remove());
  transition.ready.then(() => {
    const radius = Math.ceil(
      Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)) / GRADIENT_OFFSET,
    );
    document.documentElement.animate(
      {
        maskImage: [MASK_URL, MASK_URL],
        maskRepeat: ["no-repeat", "no-repeat"],
        maskPosition: [`${x}px ${y}px`, `${x - radius}px ${y - radius}px`],
        maskSize: ["0px", `${2 * radius}px`],
      },
      { duration: DURATION_MS, easing: "ease-in", pseudoElement: "::view-transition-new(root)" },
    );
  });
}

/** Centre of the command palette's highlighted row, read before the palette closes. */
export function selectedPaletteRow(): { x: number; y: number } | undefined {
  const row = document.querySelector("[cmdk-item][data-selected=true]")?.getBoundingClientRect();
  return row && { x: row.left + row.width / 2, y: row.top + row.height / 2 };
}

function resolve(choice: ThemeChoice, systemTheme: string | undefined): "light" | "dark" {
  if (choice === "system") return systemTheme === "dark" ? "dark" : "light";
  return choice;
}

/**
 * `setTheme` with the reveal. Switches instantly when the resolved theme doesn't change
 * (e.g. picking "system" while it already matches).
 */
export function useThemeSwitch() {
  const { setTheme, resolvedTheme, systemTheme } = useTheme();

  return useCallback(
    (choice: ThemeChoice, origin?: RevealOrigin) => {
      const next = resolve(choice, systemTheme);
      if (next === resolvedTheme) {
        setTheme(choice);
        return;
      }
      revealChange(() => {
        // next-themes applies its class in an effect; the new snapshot is taken when this returns,
        // so render synchronously and set the class here rather than wait for it
        flushSync(() => setTheme(choice));
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(next);
      }, origin);
    },
    [setTheme, resolvedTheme, systemTheme],
  );
}
